// src/pages/QuickItemCheckout.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    FiPackage, FiTruck, FiFileText, FiCheck, FiAlertTriangle, 
    FiHash, FiHome, FiArrowRight, FiSearch, FiX, FiMapPin,
    FiArrowLeft, FiLayers 
} from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { AuthContext } from '../context/AuthContext';
import Modal from '../components/Modal';

// --- Ticket Selection Modal ---
const TicketSelectModal = ({ isOpen, onClose, tickets, onSelect, currentTicketId }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredTickets = useMemo(() => {
        const query = searchTerm.toLowerCase();
        return tickets.filter(t => 
            t.subject.toLowerCase().includes(query) || 
            t.client.toLowerCase().includes(query)
        );
    }, [searchTerm, tickets]);

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="Select Active Ticket" 
            size="standard"
        >
            <div className="search-input-wrapper modal-search-wrapper" style={{ marginBottom: '1rem' }}>
                <FiSearch className="search-icon" />
                <input 
                    type="text" 
                    className="search-input" 
                    placeholder="Search by subject or client..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
            </div>
            
            <div className="selection-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredTickets.length > 0 ? (
                    filteredTickets.map(ticket => (
                        <div 
                            key={ticket._id} 
                            className={`selection-list-item ${currentTicketId === ticket._id ? 'selected' : ''}`} 
                            onClick={() => onSelect(ticket)}
                        >
                            <div className="item-main-info">
                                <p className="item-title">{ticket.subject}</p>
                                <p className="item-subtitle">{ticket.client}</p>
                            </div>
                            <div className="item-meta-info">
                                <span className={`status-badge status-${ticket.status.toLowerCase().replace(/ /g, '-')}`}>
                                    {ticket.status}
                                </span>
                                {currentTicketId === ticket._id && <FiCheck className="selection-checkmark" />}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="selection-list-empty">No active tickets found matching your search.</div>
                )}
            </div>
        </Modal>
    );
};

const DestinationSelectModal = ({ isOpen, onClose, offices, trucks, communities, onSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    
    const filteredOptions = useMemo(() => {
        const query = searchTerm.toLowerCase();
        const results = { offices: [], trucks: [], communities: [] };
        
        results.offices = offices.filter(o => o.name.toLowerCase().includes(query));
        results.trucks = trucks.filter(t => t.name.toLowerCase().includes(query));
        results.communities = communities.filter(c => c.name.toLowerCase().includes(query));
        
        return results;
    }, [searchTerm, offices, trucks, communities]);

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="Select Destination" 
            size="standard"
        >
            <div className="search-input-wrapper modal-search-wrapper" style={{ marginBottom: '1rem' }}>
                <FiSearch className="search-icon" />
                <input 
                    type="text" 
                    className="search-input" 
                    placeholder="Search locations..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
            </div>
            
            <div className="selection-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredOptions.offices.length > 0 && (
                    <div className="group-label" style={{ padding: '0.5rem', fontWeight: 'bold', fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>Offices</div>
                )}
                {filteredOptions.offices.map(site => (
                    <div key={site._id} className="selection-list-item" onClick={() => onSelect(site)}>
                        <div className="item-main-info">
                            <p className="item-title"><FiHome style={{marginRight: '8px'}}/> {site.name}</p>
                        </div>
                    </div>
                ))}

                {filteredOptions.trucks.length > 0 && (
                    <div className="group-label" style={{ padding: '0.5rem', fontWeight: 'bold', fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginTop: '1rem' }}>Trucks</div>
                )}
                {filteredOptions.trucks.map(site => (
                    <div key={site._id} className="selection-list-item" onClick={() => onSelect(site)}>
                        <div className="item-main-info">
                            <p className="item-title"><FiTruck style={{marginRight: '8px'}}/> {site.name}</p>
                        </div>
                    </div>
                ))}

                {filteredOptions.communities.length > 0 && (
                    <div className="group-label" style={{ padding: '0.5rem', fontWeight: 'bold', fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginTop: '1rem' }}>Communities</div>
                )}
                {filteredOptions.communities.map(comm => (
                    <div key={comm._id} className="selection-list-item" onClick={() => onSelect(comm)}>
                        <div className="item-main-info">
                            <p className="item-title"><FiMapPin style={{marginRight: '8px'}}/> {comm.name}</p>
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
};

const QuickItemCheckout = ({ sites }) => {
    const { masterItemId, unitId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const callApi = useApi();
    const { user } = useContext(AuthContext);
    const [itemData, setItemData] = useState(null);
    const [units, setUnits] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [sourceContext, setSourceContext] = useState('unknown'); 
    const [sourceLocationName, setSourceLocationName] = useState('');
    const [targetAction, setTargetAction] = useState('ticket'); 
    const [quantity, setQuantity] = useState(1);
    const [ticketId, setTicketId] = useState('');
    const [userTruck, setUserTruck] = useState(null);
    const [offices, setOffices] = useState([]);
    const [trucks, setTrucks] = useState([]); 
    const [communities, setCommunities] = useState([]);
    const [destination, setDestination] = useState(null); 
    const [isDestModalOpen, setIsDestModalOpen] = useState(false);
    const [myTickets, setMyTickets] = useState([]);
    
    // ✅ Assembly State
    const [availableAssemblies, setAvailableAssemblies] = useState([]);
    const [selectedAssemblyId, setSelectedAssemblyId] = useState('');
    const [assemblyContents, setAssemblyContents] = useState([]); 
    
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    const isAdmin = useMemo(() => 
        user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
    [user]);

    const isAssemblyItem = itemData?.itemType === 'Assembly' || itemData?.isAssembly;

    useEffect(() => {
        const fetchData = async () => {
            try {
                let targetUnitIds = [];
                if (unitId === 'bulk') {
                    const params = new URLSearchParams(location.search);
                    const idsParam = params.get('ids');
                    if (idsParam) targetUnitIds = idsParam.split(',');
                } else if (unitId) {
                    targetUnitIds = [unitId];
                }

                // ✅ FETCH SPECIFIC ASSEMBLY UNITS
                const [sitesRes, techRes, commsRes, invRes, masterRes, asmRes] = await Promise.all([
                    callApi('sites'), 
                    callApi('technicians'),
                    callApi('communities'),
                    callApi('inventory'), 
                    callApi('master-inventory'),
                    callApi('inventory/assemblies/active')
                ]);
                
                let myTruck = null;
                let myTechId = null;

                if (sitesRes.success && techRes.success) {
                    setOffices(sitesRes.data.filter(s => s.type === 'Office'));
                    setTrucks(sitesRes.data.filter(s => s.type === 'Truck'));

                    const myTech = techRes.data.find(t => t.userId?._id === user.id || t.userId === user.id);
                    if (myTech) {
                        myTechId = myTech._id;
                        myTruck = sitesRes.data.find(s => s.type === 'Truck' && s.assignedTechnician === myTech._id);
                        setUserTruck(myTruck);
                    }
                }

                if (commsRes.success) setCommunities(commsRes.data);

                // ✅ Map the specific serial-numbered boxes
                if (asmRes.success) {
                    setAvailableAssemblies(asmRes.data);
                }

                const ticketsRes = await callApi('tickets');
                if (ticketsRes.success && Array.isArray(ticketsRes.data.data)) {
                    let availableTickets = [];
                    if (isAdmin) {
                        availableTickets = ticketsRes.data.data.filter(t => t.status !== 'Closed');
                    } else if (myTechId) {
                        availableTickets = ticketsRes.data.data.filter(t => 
                            t.assignedTechnicianIds.includes(myTechId) && t.status !== 'Closed'
                        );
                    }
                    setMyTickets(availableTickets);
                    if (availableTickets.length === 1) {
                        setTicketId(availableTickets[0]._id);
                        setSelectedTicket(availableTickets[0]);
                    }
                }

                let foundUnits = [];
                let foundMasterItem = null;
                let determinedSource = 'office';
                let locName = 'Main Office';
                let sourceTruckId = null;

                if (targetUnitIds.length > 0) {
                    const unitPromises = targetUnitIds.map(id => callApi(`inventory/unit/${id}`));
                    const results = await Promise.all(unitPromises);
                    foundUnits = results.filter(r => r.success).map(r => r.data);
                    setUnits(foundUnits);
                    setQuantity(foundUnits.length);

                    if (foundUnits.length > 0) {
                        const firstUnit = foundUnits[0];
                        const currentOwner = sitesRes.data?.find(s => s._id === firstUnit.ownerId);

                        if (firstUnit.ownerType === 'Community') {
                             determinedSource = 'installed';
                             const comm = commsRes.data?.find(c => c._id === firstUnit.ownerId);
                             locName = comm ? comm.name : 'Client Site';
                        } else if (currentOwner) {
                            locName = currentOwner.name;
                            if (currentOwner.type === 'Office') determinedSource = 'office';
                            else if (currentOwner.type === 'Truck') {
                                determinedSource = 'truck';
                                sourceTruckId = currentOwner._id;
                            }
                        } else if (firstUnit.ownerType === 'InventoryUnit') { // ✅ Support parts inside specific assemblies
                             determinedSource = 'assembly';
                             const pRes = await callApi(`inventory/unit/${firstUnit.ownerId}`);
                             if (pRes.success && pRes.data) {
                                 locName = `Assembly: ${pRes.data.serialNumber || pRes.data.name}`;
                             } else {
                                 locName = 'Assembly';
                             }
                        } else if (firstUnit.ownerType === 'Inventory') {
                             determinedSource = 'assembly';
                             const asm = invRes.data?.find(a => a._id === firstUnit.ownerId);
                             locName = asm ? `Assembly: ${asm.name}` : 'Assembly';
                        }
                        
                        if (masterRes.success) {
                            foundMasterItem = masterRes.data.find(m => m._id === firstUnit.masterInventoryItemId);
                        }

                        // FALLBACK FOR CUSTOM ASSEMBLIES
                        if (!foundMasterItem) {
                            const aggItem = invRes.data?.find(i => i.masterInventoryItemId === firstUnit.masterInventoryItemId);
                            foundMasterItem = {
                                _id: firstUnit.masterInventoryItemId,
                                name: firstUnit.name,
                                sku: firstUnit.sku || 'CUSTOM-ASSEMBLY',
                                itemType: 'Assembly',
                                imageUrl: aggItem ? aggItem.imageUrl : null
                            };
                        }

                        // Fetch ALL children so the user sees everything inside the box
                        if (foundMasterItem.itemType === 'Assembly' || foundMasterItem.isAssembly) {
                            let allContents = [];
                            
                            // 1. Get unit specific children
                            const childRes = await callApi(`inventory/unit/${firstUnit._id}/children`);
                            if (childRes.success) allContents.push(...childRes.data);

                            // 2. Get aggregate children
                            const agg = invRes.data?.find(i => i.masterInventoryItemId === firstUnit.masterInventoryItemId && i.ownerId === firstUnit.ownerId);
                            if (agg) {
                                const aggChildRes = await callApi(`inventory/${agg._id}/units`);
                                if (aggChildRes.success) {
                                    const aggChildren = aggChildRes.data.filter(u => u.ownerType === 'Inventory');
                                    allContents.push(...aggChildren);
                                }
                            }
                            setAssemblyContents(allContents);
                        }
                    }
                } else if (masterItemId) {
                    const agg = invRes.data?.find(i => i._id === masterItemId);
                    if (agg) {
                        foundMasterItem = masterRes.data.find(m => m._id === agg.masterInventoryItemId);
                        if (!foundMasterItem) {
                            foundMasterItem = {
                                _id: agg.masterInventoryItemId,
                                name: agg.name,
                                sku: agg.sku || 'CUSTOM-ASSEMBLY',
                                itemType: 'Assembly',
                                imageUrl: agg.imageUrl
                            };
                        }

                        if (foundMasterItem.itemType === 'Assembly' || agg.isAssembly) {
                            const unitsRes = await callApi(`inventory/${agg._id}/units`);
                            if (unitsRes.success && unitsRes.data.length > 0) {
                                const firstUnit = unitsRes.data.find(u => u.status === 'Available');
                                if (firstUnit) {
                                    let allContents = [];
                                    const childRes = await callApi(`inventory/unit/${firstUnit._id}/children`);
                                    if (childRes.success) allContents.push(...childRes.data);

                                    const aggChildren = unitsRes.data.filter(u => u.ownerType === 'Inventory');
                                    allContents.push(...aggChildren);
                                    
                                    setAssemblyContents(allContents);
                                }
                            }
                        }

                        const currentOwner = sitesRes.data?.find(s => s._id === agg.ownerId);
                        if (currentOwner) {
                            locName = currentOwner.name;
                            if (currentOwner.type === 'Office') determinedSource = 'office';
                            else if (currentOwner.type === 'Truck') {
                                determinedSource = 'truck';
                                sourceTruckId = currentOwner._id;
                            }
                        } else if (agg.ownerType === 'Community') {
                            determinedSource = 'installed';
                            const comm = commsRes.data?.find(c => c._id === agg.ownerId);
                            locName = comm ? comm.name : 'Client Site';
                        } else if (agg.ownerType === 'InventoryUnit') { // ✅ Support parts inside specific assemblies
                             determinedSource = 'assembly';
                             const parentUnitRes = await callApi(`inventory/unit/${agg.ownerId}`);
                             if (parentUnitRes.success && parentUnitRes.data) {
                                 locName = `Assembly: ${parentUnitRes.data.serialNumber || parentUnitRes.data.name}`;
                             }
                        } else if (agg.ownerType === 'Inventory') {
                            determinedSource = 'assembly';
                            const parentAgg = invRes.data?.find(a => a._id === agg.ownerId);
                            locName = parentAgg ? `Assembly: ${parentAgg.name}` : 'Assembly';
                        }
                    }
                }

                setItemData(foundMasterItem);
                setSourceContext(determinedSource);
                setSourceLocationName(locName);

                // --- SECURITY ACTION LOGIC ---
                if (determinedSource === 'installed') {
                    setTargetAction('return');
                } else if (isAdmin) {
                   setTargetAction('ticket');
                } else {
                    if (determinedSource === 'office') setTargetAction('truck');
                    else if (determinedSource === 'truck' && myTruck && sourceTruckId !== myTruck._id) setTargetAction('truck');
                    else setTargetAction('ticket');
                }

            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [masterItemId, unitId, location.search, user.id, callApi, isAdmin]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        let sourceInventoryId;
        const invRes = await callApi('inventory');
        if(!invRes.success) return alert("Failed to fetch inventory.");

        if (units.length > 0) {
            const agg = invRes.data.find(i => 
                i.masterInventoryItemId === units[0].masterInventoryItemId && 
                i.ownerId === units[0].ownerId
            );
            if (agg) sourceInventoryId = agg._id;
            else return alert("System Error: Could not locate inventory record.");
        } else {
            const sourceItem = invRes.data.find(i => 
                i._id === masterItemId || // Match aggregate direct if used
                (i.masterInventoryItemId === itemData._id && 
                 i.ownerType === 'Site' && 
                 i.quantity >= quantity &&
                 sites.find(s => s._id === i.ownerId)?.type === 'Office')
            );
            if (sourceItem) sourceInventoryId = sourceItem._id;
        }

        if (!sourceInventoryId) return alert("Item out of stock or source not found.");
        
        const payloadBase = {
            sourceInventoryId,
            quantity,
            specificUnitIds: units.map(u => u._id)
        };

        if (targetAction === 'ticket') {
            if (!ticketId) return alert("Please select a ticket.");
            const res = await callApi(`tickets/${ticketId}/inventory`, 'POST', {
                inventoryId: sourceInventoryId, 
                quantityUsed: quantity,
                specificUnitIds: units.map(u => u._id)
            });
            if (res.success) navigate(`/tickets/${ticketId}`); else alert(res.error);
        } 
        else if (targetAction === 'assembly') {
            // ✅ NEW: Transfer directly to an Assembly
            if (!selectedAssemblyId) return alert("Please select a target assembly.");
            
            const res = await callApi('inventory/transfer', 'POST', {
                ...payloadBase,
                destinationSiteId: selectedAssemblyId,
                destinationType: 'InventoryUnit' // Treat the assembly unit as the owner
            });
            
            if (res.success) navigate('/inventory');
            else alert(res.error);
        }
        else {
            let destinationId;

            if (sourceContext === 'installed') {
                if (!userTruck) {
                    return alert("You must have an assigned truck to scan items out from a site.");
                }
                destinationId = userTruck._id;
            } else {
                destinationId = (isAdmin || targetAction === 'return') ? destination?._id : (userTruck?._id);
            }

            if (!destinationId) return alert("No destination selected.");

            const isCommunity = communities.some(c => c._id === destinationId);

            const res = await callApi('inventory/transfer', 'POST', {
                ...payloadBase,
                destinationSiteId: destinationId,
                destinationType: isCommunity ? 'Community' : 'Site'
            });
            
            if (res.success) navigate('/inventory', { state: { siteId: destinationId } });
            else alert(res.error);
        }
    };

    if (loading) return <div className="page-container"><div className="loading-indicator">Loading...</div></div>;
    if (!itemData) return <div className="page-container"><div className="no-tasks-message">Item not found.</div></div>;

    const getHeaderDetails = () => {
        if (sourceContext === 'office') return { color: 'var(--blue-600)', text: 'Restocking from Office', icon: <FiHome size={24} /> };
        if (sourceContext === 'truck') return { color: 'var(--orange-600)', text: 'Using from Truck', icon: <FiTruck size={24} /> };
        if (sourceContext === 'installed') return { color: 'var(--green-600)', text: 'Managing Installed Item', icon: <FiCheck size={24} /> };
        if (sourceContext === 'assembly') return { color: 'var(--purple-600)', text: 'Pulling from Assembly', icon: <FiLayers size={24} /> };
        return { color: 'var(--gray-600)', text: 'Loading...', icon: <FiPackage size={24} /> };
    };

    const { color, text, icon } = getHeaderDetails();
    const isMyTruck = userTruck && units.length > 0 && units[0].ownerId === userTruck._id;

    return (
        <div className="page-container" style={{maxWidth: '600px', margin: '0 auto'}}>
            <div className="page-header">
                <div className="header-left-details">
                    <button onClick={() => navigate(-1)} className="back-btn" title="Go Back">
                        <FiArrowLeft />
                    </button>
                    <div>
                        <h2 className="page-title">Quick Checkout</h2>
                    </div>
                </div>
            </div>

            <DestinationSelectModal 
                isOpen={isDestModalOpen} 
                onClose={() => setIsDestModalOpen(false)}
                offices={offices}
                trucks={isAdmin ? trucks : []}
                communities={isAdmin ? communities : []}
                onSelect={(loc) => { setDestination(loc); setIsDestModalOpen(false); }}
            />

            <TicketSelectModal 
                isOpen={isTicketModalOpen}
                onClose={() => setIsTicketModalOpen(false)}
                tickets={myTickets}
                currentTicketId={ticketId}
                onSelect={(t) => {
                    setTicketId(t._id);
                    setSelectedTicket(t);
                    setIsTicketModalOpen(false);
                }}
            />

            <div style={{ backgroundColor: color, color: 'white', padding: '1rem', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                {icon}
                <div>
                    <h3 style={{margin: 0, fontSize: '1rem'}}>{text}</h3>
                    <p style={{margin: 0, fontSize: '0.8rem', opacity: 0.9}}>Source: {sourceLocationName}</p>
                </div>
            </div>

            <div className="card" style={{borderTopLeftRadius: 0, borderTopRightRadius: 0}}>
                <div className="card-body">
                    <div style={{display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center'}}>
                        <div className="list-view-image-thumbnail-wrapper" style={{width: '80px', height: '80px'}}>
                            {itemData.imageUrl ? <img src={itemData.imageUrl} className="list-view-image-thumbnail" alt={itemData.name}/> : <FiPackage size={32} color="var(--gray-400)"/>}
                        </div>
                        <div>
                            <h2 style={{fontSize: '1.25rem', marginBottom: '0.25rem'}}>{itemData.name}</h2>
                            <p className="item-meta">SKU: {itemData.sku}</p>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'4px', marginTop:'0.5rem'}}>
                                {units.map(u => (
                                    <span key={u._id} className="detail-tag"><FiHash size={12}/> SN: {u.serialNumber}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {assemblyContents.length > 0 && (
                        <div style={{ marginBottom: '1.5rem', backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                            <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-800)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FiLayers /> Assembly Contents
                            </h4>
                            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                {assemblyContents.map(c => (
                                    <div key={c._id} style={{ fontSize: '0.85rem', color: 'var(--gray-600)', display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px dashed var(--gray-200)' }}>
                                        <span>↳ {c.name}</span>
                                        <span style={{ fontFamily: 'monospace' }}>{c.serialNumber || c.sku || 'N/A'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label className="form-label">Action</label>
                            {isAdmin ? (
                                <div className="segmented-control" style={{width: '100%', display: 'flex', marginBottom: '1rem'}}>
                                    <button type="button" className={`segment-btn ${targetAction === 'ticket' ? 'active' : ''}`} style={{flex:1, justifyContent: 'center'}} onClick={() => setTargetAction('ticket')}><FiFileText/> Use on Ticket</button>
                                    <button type="button" className={`segment-btn ${targetAction === 'transfer' ? 'active' : ''}`} style={{flex:1, justifyContent: 'center'}} onClick={() => setTargetAction('transfer')}><FiTruck/> Transfer</button>
                                    {/* ✅ NEW: Add to Assembly Toggle */}
                                    <button type="button" className={`segment-btn ${targetAction === 'assembly' ? 'active' : ''}`} style={{flex:1, justifyContent: 'center'}} onClick={() => setTargetAction('assembly')}><FiLayers/> Add to Assembly</button>
                                </div>
                            ) : (
                                <>
                                    {sourceContext === 'office' && (
                                        <div style={{padding: '0.75rem', border: '1px solid var(--blue-200)', backgroundColor: 'var(--blue-50)', borderRadius: '6px', color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            <FiArrowRight /> <strong>Transferring to: {userTruck ? userTruck.name : 'My Truck'}</strong>
                                        </div>
                                    )}

                                    {sourceContext === 'truck' && isMyTruck && (
                                        <div className="segmented-control" style={{width: '100%', display: 'flex', marginBottom: '1rem'}}>
                                            <button type="button" className={`segment-btn ${targetAction === 'ticket' ? 'active' : ''}`} style={{flex:1, justifyContent: 'center'}} onClick={() => setTargetAction('ticket')}><FiFileText/> Use on Ticket</button>
                                            <button type="button" className={`segment-btn ${targetAction === 'return' ? 'active' : ''}`} style={{flex:1, justifyContent: 'center'}} onClick={() => setTargetAction('return')}><FiHome/> Return to Office</button>
                                            {/* ✅ NEW: Even non-admins can build assemblies if they have the parts on their truck */}
                                            <button type="button" className={`segment-btn ${targetAction === 'assembly' ? 'active' : ''}`} style={{flex:1, justifyContent: 'center'}} onClick={() => setTargetAction('assembly')}><FiLayers/> Build Assembly</button>
                                        </div>
                                    )}

                                    {sourceContext === 'truck' && !isMyTruck && (
                                        <div style={{padding: '0.75rem', border: '1px solid var(--orange-200)', backgroundColor: 'var(--orange-50)', borderRadius: '6px', color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            <FiArrowRight /> <strong>Transferring to: {userTruck ? userTruck.name : 'My Truck'}</strong>
                                        </div>
                                    )}

                                    {sourceContext === 'installed' && (
                                        <div style={{padding: '0.75rem', border: '1px solid var(--green-200)', backgroundColor: 'var(--green-50)', borderRadius: '6px', color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            <FiArrowRight /> <strong>Returning Item to Your Truck</strong>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Quantity</label>
                            <input type="number" className="form-input" min="1" value={quantity} disabled={units.length > 0} onChange={(e) => setQuantity(Number(e.target.value))} />
                        </div>
                        
                        {targetAction === 'ticket' && sourceContext !== 'installed' && (
                            <div className="form-group">
                                <label className="form-label">Select Ticket</label>
                                {myTickets.length > 0 ? (
                                    <div 
                                        className="contact-display-box" 
                                        onClick={() => setIsTicketModalOpen(true)}
                                        style={{ 
                                            cursor: 'pointer', 
                                            display: 'flex', 
                                            flexDirection: 'row', 
                                            alignItems: 'center', 
                                            gap: '0.75rem' 
                                        }}
                                    >
                                        <FiSearch size={16} color="var(--gray-400)" />
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            {selectedTicket ? (
                                                <>
                                                    <span style={{ fontWeight: '500' }}>{selectedTicket.subject}</span>
                                                     <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{selectedTicket.client}</span>
                                                </>
                                            ) : (
                                                <span>Click to Select Active Ticket</span>
                                            )}
                                        </div>
                                    </div>
                                ) : ( 
                                    <div className="error-banner">{isAdmin ? 'No active tickets found in system.' : 'No active tickets assigned to you.'}</div>
                                )}
                            </div>
                        )}

                        {/* ✅ NEW: Point directly to specific SN assemblies! */}
                        {targetAction === 'assembly' && (
                            <div className="form-group">
                                <label className="form-label">Select Target Assembly</label>
                                <select 
                                    className="form-select" 
                                    value={selectedAssemblyId} 
                                    onChange={(e) => setSelectedAssemblyId(e.target.value)}
                                    required
                                >
                                    <option value="">-- Choose Specific Assembly --</option>
                                    {availableAssemblies.map(a => {
                                        let loc = 'Unknown';
                                        if (a.ownerType === 'Site') {
                                            const s = sites.find(site => site._id === a.ownerId);
                                            if (s) loc = s.name;
                                        } else if (a.ownerType === 'Community') {
                                            const c = communities.find(comm => comm._id === a.ownerId);
                                            if (c) loc = c.name;
                                        }
                                        return (
                                            <option key={a._id} value={a._id}>{a.name} (SN: {a.serialNumber}) • {loc}</option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}

                        {(targetAction === 'return' || (isAdmin && targetAction === 'transfer')) && sourceContext !== 'installed' && (
                            <div className="form-group">
                                <label className="form-label">Select Destination</label>
                                <div 
                                    className="contact-display-box"
                                    onClick={() => setIsDestModalOpen(true)}
                                    style={{ 
                                        cursor: 'pointer', 
                                        display: 'flex', 
                                        flexDirection: 'row', 
                                        alignItems: 'center', 
                                        gap: '0.75rem' 
                                    }}
                                >
                                    <FiSearch size={16} color="var(--gray-400)" />
                                    <span>{destination ? destination.name : 'Click to Select Destination'}</span>
                                </div>
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="btn btn-primary btn-block btn-lg" 
                            style={{marginTop: '1rem'}} 
                            disabled={
                                loading || 
                                (targetAction === 'ticket' && !ticketId) ||
                                (targetAction === 'assembly' && !selectedAssemblyId) ||
                                (targetAction !== 'ticket' && targetAction !== 'assembly' && !destination && sourceContext !== 'installed' && (isAdmin || targetAction === 'return'))
                            }
                        >
                            <FiCheck className="btn-icon"/> Confirm Checkout
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default QuickItemCheckout;